import type { ErrorResponsePayload, StreamErrorDetails } from "@/api/models";

export const GENERIC_ERROR_MESSAGE = "Something went wrong, please try again.";

const ERROR_MESSAGES: Record<string, string> = {
  capacity: "This model may currently be at capacity, please wait a minute or try a different model.",
  context_length_exceeded: "This request is too large for this model. Try a shorter message or a different model.",
  max_output_tokens: "The response was too long for this request. Try again with a shorter prompt or ask for a shorter response.",
  empty_response: GENERIC_ERROR_MESSAGE,
  invalid_prompt: "I couldn't complete that request. Please rephrase it and try again.",
  content_filter: "I couldn't complete that request. Please rephrase it and try again.",
  content_filter_prompt: "I couldn't complete that request. Please rephrase it and try again.",
  refusal: "I couldn't complete that request. Please rephrase it and try again.",
  upstream_timeout: "The model service is temporarily unavailable. Please try again in a moment.",
  upstream_unreachable: "The model service is temporarily unavailable. Please try again in a moment.",
  deployment_unavailable: "This model is currently unavailable. Please try again or select another model.",
  provider_auth: "This model is currently unavailable. Please try again or select another model.",
  file_workspace_capacity: "File processing is temporarily unavailable. Please try again in a moment.",
  file_workspace_unavailable: "File processing is temporarily unavailable. Please try again in a moment.",
  tool_calls: "This model couldn't use a requested tool. Try a different model or try again.",
};

export class ApiRequestError extends Error {
  readonly errorDetails?: StreamErrorDetails;
  readonly statusCode?: number;
  readonly errorCode?: string;
  readonly errorType?: string;

  constructor(
    message: string,
    options: { details?: StreamErrorDetails; statusCode?: number; errorCode?: string; errorType?: string } = {},
  ) {
    super(message);
    this.name = "ApiRequestError";
    this.errorDetails = options.details;
    this.statusCode = options.statusCode;
    this.errorCode = options.errorCode;
    this.errorType = options.errorType;
  }
}

export const apiRequestErrorFromResponse = (
  response: Response,
  payload: Partial<ErrorResponsePayload> | null,
  fallbackMessage = GENERIC_ERROR_MESSAGE,
): ApiRequestError => new ApiRequestError(
  typeof payload?.error === "string" && payload.error.trim() ? payload.error : fallbackMessage,
  {
    details: payload?.error_details,
    statusCode: response.status,
    errorCode: payload?.error_code,
    errorType: payload?.error_type,
  },
);

const getErrorMessageText = (error: unknown): string | undefined => {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object" && "error" in error && typeof error.error === "string") {
    return error.error;
  }
  return undefined;
};

export const getErrorSummary = (
  error: unknown,
  details?: StreamErrorDetails,
): string => {
  const errorObject = error && typeof error === "object" ? error as Record<string, unknown> : undefined;
  const reason = details?.reason || (errorObject?.errorDetails as StreamErrorDetails | undefined)?.reason;
  if (reason && ERROR_MESSAGES[reason]) return ERROR_MESSAGES[reason];
  const errorCode = details?.error_code || errorObject?.errorCode || errorObject?.error_code;
  if (errorCode === "weekly_token_limit_reached") {
    return "Weekly token limit reached. Resets Monday 00:00 UTC.";
  }

  const rawMessage = getErrorMessageText(error)?.toLowerCase() || "";
  // Legacy endpoints may not have a reason code yet. Keep these matches narrow
  // so a generic Foundry error does not get labeled as a capacity issue.
  if (/model.{0,40}capacity|capacity.{0,40}model|insufficient.{0,20}capacity/.test(rawMessage)) {
    return ERROR_MESSAGES.capacity;
  }
  if (/timed?\s*out|timeout/.test(rawMessage)) return ERROR_MESSAGES.upstream_timeout;
  if (/failed to fetch|networkerror|connection (?:failed|lost|refused)/.test(rawMessage)) {
    return ERROR_MESSAGES.upstream_unreachable;
  }
  return GENERIC_ERROR_MESSAGE;
};

export const getErrorDetails = (
  error: unknown,
  details?: StreamErrorDetails,
  statusCode?: number,
): StreamErrorDetails => {
  const errorObject = error && typeof error === "object" ? error as Record<string, unknown> : undefined;
  const errorDetails = details || errorObject?.errorDetails as StreamErrorDetails | undefined;
  const code = statusCode ?? (typeof errorObject?.statusCode === "number" ? errorObject.statusCode : undefined);
  const rawMessage = getErrorMessageText(error);
  const hasDiagnostics = Boolean(
    rawMessage || code || (errorDetails && Object.values(errorDetails).some((value) => value !== undefined && value !== "")),
  );
  if (!hasDiagnostics) return {};

  return {
    ...errorDetails,
    reason: errorDetails?.reason || (rawMessage ? "unknown" : undefined),
    error_type: errorDetails?.error_type || (typeof errorObject?.errorType === "string" ? errorObject.errorType : undefined),
    error_code: errorDetails?.error_code || (typeof errorObject?.errorCode === "string" ? errorObject.errorCode : undefined),
    status_code: errorDetails?.status_code || code,
    // Keep an existing provider detail intact. The response error is a useful
    // fallback for older endpoints that returned only a string.
    upstream_detail: errorDetails?.upstream_detail || rawMessage,
  };
};
